import { Button, FormField, Input, Stack } from '@volee/ui'

import { FormError } from '@volee/forms'
import { NewPasswordFields, PasswordField } from './password-fields.tsx'
import type {
  AuthFormProps,
  MfaFields,
  NewPasswordFields as NewPasswordValues,
  SignInFields,
} from '../data/auth-fields.ts'
import { messages } from '../messages/index.ts'

// The three states of the sign-in. Presentation only — `data/use-sign-in.ts` owns the behaviour,
// and apps/admin-panel/docs/login-sta-dentro-il-prodotto.md says why none of them is a route.

export function SignInForm({ form, submit, pending, formError }: AuthFormProps<SignInFields>) {
  const { register, formState } = form

  return (
    <form noValidate onSubmit={form.handleSubmit(submit)}>
      <Stack gap="md">
        <FormError message={formError} />

        <FormField label={messages.signIn.email} error={formState.errors.email?.message} required>
          {(props) => (
            <Input
              {...props}
              {...register('email')}
              type="email"
              autoComplete="username"
              disabled={pending}
            />
          )}
        </FormField>

        <PasswordField
          label={messages.signIn.password}
          registration={register('password')}
          error={formState.errors.password?.message}
          pending={pending}
          autoComplete="current-password"
        />

        <Button type="submit" loading={pending}>
          {messages.signIn.submit}
        </Button>
      </Stack>
    </form>
  )
}

export function NewPasswordForm({
  form,
  submit,
  pending,
  formError,
}: AuthFormProps<NewPasswordValues>) {
  const { register, formState } = form

  return (
    <form noValidate onSubmit={form.handleSubmit(submit)}>
      <Stack gap="md">
        <FormError message={formError} />

        <NewPasswordFields
          newPassword={register('newPassword')}
          confirmPassword={register('confirmPassword')}
          errors={{
            newPassword: formState.errors.newPassword?.message,
            confirmPassword: formState.errors.confirmPassword?.message,
          }}
          pending={pending}
        />

        <Button type="submit" loading={pending}>
          {messages.newPassword.submit}
        </Button>
      </Stack>
    </form>
  )
}

export function MfaForm({ form, submit, pending, formError }: AuthFormProps<MfaFields>) {
  const { register, formState } = form

  return (
    <form noValidate onSubmit={form.handleSubmit(submit)}>
      <Stack gap="md">
        <FormError message={formError} />

        <FormField label={messages.mfa.code} error={formState.errors.code?.message} required>
          {(props) => (
            <Input
              {...props}
              {...register('code')}
              // The browser offers the code from the OS when the app is on the same device.
              autoComplete="one-time-code"
              inputMode="numeric"
              autoFocus
              disabled={pending}
            />
          )}
        </FormField>

        <Button type="submit" loading={pending}>
          {messages.mfa.submit}
        </Button>
      </Stack>
    </form>
  )
}
